import React, { useState, useEffect } from 'react'

import { Outlet, useNavigate, useParams } from 'react-router-dom'

import useFirebase from 'hooks/firebase'

import {
  Button,
  Card,
  Checkbox,
  CardContent,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material'

import LoadingButton from '@mui/lab/LoadingButton'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'

import PersonalInformation from 'widgets/personal'
import AssignedStudentsTable from 'widgets/students/table'

const ParentDetails = (props) => {
  const { director } = props
  const params = useParams()
  const id = params.id

  const navigate = useNavigate()

  const { firestore, functions } = useFirebase()
  const {
    getParent,
    getStudentsBySchoolId,
    parent,
    students,
    updateDocFromCollection,
  } = firestore
  const { deleteProfile } = functions

  const [showUnassigned, setShowUnassigned] = useState(false)
  const handleShowUnassigned = (event) => {
    setShowUnassigned(event.target.checked)
  }

  const [assignedStudents, setAssignedStudents] = useState([])
  const [unassignedStudents, setUnassignedStudents] = useState([])

  const [rows, setRows] = useState([])

  const [selectedStudents, setSelectedStudents] = useState([])
  const [assigning, setAssigning] = useState(false)

  useEffect(() => {
    if (director === undefined) return
    getStudentsBySchoolId(director.schoolId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [director])

  useEffect(() => {
    getParent(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (students === undefined) return
    refreshStudents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students])

  useEffect(() => {
    if (showUnassigned) {
      setRows(unassignedStudents)
    } else {
      setRows(assignedStudents)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUnassigned])

  const refreshStudents = () => {
    var assignedData = []
    var unassignedData = []
    students.forEach((student) => {
      const formatted = {
        'id': student['id'],
        'name': student['name'],
        'email': student['email'],
        'stage': student['level'],
        'preceptor':  student['preceptor'] !== undefined ? student['preceptor']['name'] : 'Sin asignar',
      }

      const parents = student['parents']
      if (parents === undefined) {
        unassignedData.push(formatted)
      } else if (parents.filter(parent => parent.id === id).length > 0) {
        assignedData.push(formatted)
      } else {
        unassignedData.push(formatted)
      }
    })
    setAssignedStudents(assignedData)
    setUnassignedStudents(unassignedData)
    setRows(assignedData)
  }

  const [deleting, setDeleting] = useState(false)
  const onDelete = async () => {
    setDeleting(true)
    await deleteProfile({
      'uid': id,
      'role': 'parent'
    })
    setDeleting(false)
    navigate(-1)
  }

  const addStudentsToParent = async () => {
    const p = {
      'id': parent.id,
      'name': parent.name,
      'active': true
    }

    const newStudents = []
    selectedStudents.forEach(async studentId => {
      const student = students.filter(student => student.id === studentId)[0]
      const newStudent = {
        'id': student.id,
        'name': student.name
      }
      newStudents.push(newStudent)

      const parents = student['parents']
      if (parents !== undefined) {
        const newParents = [...parents]
        newParents.push(p)
        await updateDocFromCollection('students', student.id, {
          'parents': newParents
        })
      } else {
        await updateDocFromCollection('students', student.id, {
          'parents': [p]
        })
      }
    })

    assignedStudents.forEach(assigned => {
      const student = students.filter(student => student.id === assigned.id)[0]

      const newStudent = {
        'id': student.id,
        'name': student.name
      }
      
      newStudents.push(newStudent)
    })

    await updateDocFromCollection('parents', id, {
      'students': newStudents
    })

    setSelectedStudents([])
  }

  const removeStudentsFromParent = async () => {
    selectedStudents.forEach(async studentId => {
      const student = students.filter(student => student.id === studentId)[0]

      const assigned = parent['students']
      const newStudents = assigned.filter(student => student.id !== studentId)

      await updateDocFromCollection('parents', id, {
        'students': newStudents
      })
      
      const parents = student['parents']
      const newParents = parents.filter(parent => parent.id !== id)

      await updateDocFromCollection('students', student.id, {
        'parents': newParents
      })
    })
    setSelectedStudents([])
  }

  const handleAssignment = async () => {
    setAssigning(true)
    if (showUnassigned) {
      await addStudentsToParent()
      setShowUnassigned(false)
    } else {
      removeStudentsFromParent()
    }
    setAssigning(false)
  }
  
  if (parent === undefined) return <Outlet />
  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        <Button onClick={() => navigate(-1)} size='large' startIcon={<ArrowBackIcon />}>
          <b>Volver</b>
        </Button>
      </Grid>
      <Grid item xs={12} md={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PersonalInformation collection='parents' profile={parent} deleting={deleting} onDelete={onDelete} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Card sx={{ borderColor: '#655dc633', borderRadius: 2, p: 2 }}>
          <CardContent sx={{ p: 0, '&:last-child': { paddingBottom: 0 } }}>
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography color='primary' variant='body1'>
                      <b style={{ fontWeight: 900 }}>LISTA DE HIJOS</b>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} align='right'>
                    <FormControlLabel control={<Checkbox checked={showUnassigned} onChange={handleShowUnassigned} size='small' />} label={<Typography variant='body2'>Mostrar alumnos no asignados al perfil</Typography>} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <AssignedStudentsTable onStudentsSelected={setSelectedStudents} selectedStudents={selectedStudents} rows={rows} />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton disabled={selectedStudents.length === 0} disableElevation loading={assigning} loadingPosition='start' startIcon={showUnassigned ? <PersonAddAlt1Icon /> : <PersonRemoveIcon />} variant='contained' onClick={handleAssignment}>
                  <b style={{ fontWeight: 900 }}>{showUnassigned ? 'ASIGNAR' : 'DESASIGNAR'}</b>
                </LoadingButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}

export default ParentDetails